import { useMemo, useState } from 'react';
import type { ExplanationItem, SystemOptionCard as CardData, SuitabilityVerdict, ScopeItem, EngineDebugPayload, ScoreBreakdownRow } from '../types';

const VERDICT_TEXT: Record<SuitabilityVerdict, string> = {
  'highest-standard': '🟢 Strong Fit for This Home',
  'possible-compromised': '🟠 Conditional Fit',
  'not-suitable': '🔴 Mismatch / Risk of Performance Compromise',
};

const PRIORITY_LABELS: Record<string, string> = {
  low_upfront_cost: 'Low upfront cost',
  low_running_cost: 'Low running cost',
  reliability_simplicity: 'Reliability & simplicity',
  hot_water_performance: 'Hot water performance',
  quiet_operation: 'Quiet operation',
  future_proofing: 'Future proofing',
  minimal_disruption: 'Minimal disruption',
  smart_controls: 'Smart controls',
  environmental_impact: 'Environmental impact',
  comfort_even_heat: 'Comfort & even heat',
};

const PLAN_LABELS: Record<string, string> = {
  loft_conversion: 'Loft conversion',
  rear_extension: 'Rear extension',
  side_extension: 'Side extension',
  garage_conversion: 'Garage conversion',
  additional_bathroom: 'Additional bathroom',
  ufh_future: 'Underfloor heating',
  cylinder_larger_future: 'Larger cylinder',
  solar_pv: 'Solar PV',
  battery_storage: 'Battery storage',
  ev_charger: 'EV charger',
  // Local derivation future plan keys
  heat_pump_ready: 'Heat pump readiness',
  solar_integration: 'Solar integration',
  capacity_growth: 'Capacity for extensions',
  dhw_growth: 'DHW growth headroom',
};


const DEFAULT_VISIBLE_EXPLANATION_ITEMS = 6;

const EXPLANATION_GROUP_LABELS: Record<ExplanationItem['kind'], string> = {
  benefit: 'Benefits',
  cost: 'Trade-offs / Costs',
  work: 'Work required',
  assumption: 'Assumptions / Missing data',
  note: 'Notes',
};

function normalizeExplanationItems(card: CardData): ExplanationItem[] {
  const structured = card.scoreBreakdown?.details?.explanationItems;
  if (structured && structured.length > 0) return structured;

  return card.becauseBullets.map((text, index) => ({
    id: `${card.id}-legacy-${index}`,
    text,
    kind: 'note' as const,
    severity: 'info' as const,
  }));
}
const TECH_SUB_LABELS: Record<string, string> = {
  circuit_match: 'Circuit compatibility',
  mains_adequacy: 'Mains pressure/flow',
  dhw_delivery: 'DHW delivery quality',
  emitter_compat: 'Emitter compatibility',
  heat_loss_compat: 'Heat loss suitability',
  standing_loss: 'Standing loss efficiency',
  integration_complexity: 'Integration complexity',
};


const STRUCTURAL_TECH_KEYS = ['circuit_match', 'dhw_delivery', 'mains_adequacy', 'emitter_compat', 'heat_loss_compat', 'integration_complexity'] as const;

function getNarrativeHeadline(card: CardData): string {
  if (card.suitability === 'highest-standard') return 'Best fit for your household pattern';
  if (card.suitability === 'possible-compromised') return 'Technically possible, but with lifestyle or system compromises';
  return 'Not a strong fit without material changes';
}

function getStructuralSummary(card: CardData): string[] {
  const details = card.scoreBreakdown?.details;
  const technical = details?.technicalSubScores ?? {};
  const lines: string[] = [];

  const architectureComponents = ['circuit_match', 'dhw_delivery']
    .map((key) => technical[key])
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (architectureComponents.length > 0) {
    const architectureFit = Math.round(architectureComponents.reduce((sum, value) => sum + value, 0) / architectureComponents.length);
    lines.push(`Architecture suitability: ${architectureFit}/100 (circuit + hot-water delivery).`);
  }

  if (typeof card.scoreBreakdown?.technicalFit === 'number') {
    lines.push(`Technical fit: ${Math.round(card.scoreBreakdown.technicalFit)}/100.`);
  }

  if (typeof card.scoreBreakdown?.priorityFit === 'number') {
    lines.push(`Household-priority fit: ${Math.round(card.scoreBreakdown.priorityFit)}/100.`);
  }

  const structuralReasons = (details?.reasons ?? []).filter((reason) => {
    const lower = reason.toLowerCase();
    if (lower.includes('standing loss')) return false;
    return STRUCTURAL_TECH_KEYS.some((key) => lower.includes(key.replace('_', ' ')));
  });
  if (structuralReasons.length > 0) {
    lines.push(structuralReasons[0]);
  }

  return lines.slice(0, 3);
}

function isDebugViewEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);

  if (params.get('dev') === '1') return true;

  try {
    return localStorage.getItem('devtools') === '1';
  } catch {
    return false;
  }
}

function formatDebugValue(value: unknown): string {
  if (value === null || value === undefined) return 'not assessed';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 'not assessed';
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  if (typeof value === 'string' || typeof value === 'boolean') return String(value);
  try {
    const seen = new WeakSet<object>();
    return JSON.stringify(value, (_key, current) => {
      if (typeof current === 'object' && current !== null) {
        if (seen.has(current)) return '[circular]';
        seen.add(current);
      }
      return current;
    }, 2);
  } catch {
    return '[unserializable]';
  }
}

type PipelineStageId = 'ui' | 'normalized' | 'enriched' | 'engine-input' | 'rules' | 'output';

type FieldExplorerRow = {
  id: string;
  field: string;
  uiValue: unknown;
  normalizedValue: unknown;
  engineValue: unknown;
  mismatch: boolean;
  derived: boolean;
  warning: boolean;
  scoreImpacting: boolean;
  reasoning: {
    stage: string;
    title: string;
    detail: unknown;
  }[];
};

const PIPELINE_STAGES: Array<{ id: PipelineStageId; label: string; icon: string }> = [
  { id: 'ui', label: 'UI Snapshot', icon: '🟦' },
  { id: 'normalized', label: 'Normalised', icon: '🟩' },
  { id: 'enriched', label: 'Enriched / Lookups', icon: '🟨' },
  { id: 'engine-input', label: 'Engine Input', icon: '🟧' },
  { id: 'rules', label: 'Rules & Maths', icon: '🟥' },
  { id: 'output', label: 'Outputs', icon: '🟪' },
];

function flattenRecord(value: unknown, prefix = ''): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return prefix ? { [prefix]: value } : {};
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, current]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      Object.assign(acc, flattenRecord(current, path));
      return acc;
    }
    acc[path] = current;
    return acc;
  }, {});
}

function normalizeForCompare(value: unknown): string {
  if (typeof value === 'number' && !Number.isFinite(value)) return 'not-assessed';
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return formatDebugValue(value);
}

function buildFieldExplorerRows(debug: EngineDebugPayload): FieldExplorerRow[] {
  const uiFields = flattenRecord(debug.context);
  const engineFields = flattenRecord(debug.engineInput ?? {});
  const normalizedFields = flattenRecord(debug.componentBreakdown ?? {});
  const componentList = Array.isArray(debug.components) ? debug.components : [];

  const scoreImpactFields = new Set<string>();
  componentList.forEach((component) => {
    if (!component || typeof component !== 'object') return;
    const inputs = (component as { inputs?: unknown }).inputs;
    Object.keys(flattenRecord(inputs)).forEach((path) => scoreImpactFields.add(path));
  });

  const allFieldKeys = new Set<string>([
    ...Object.keys(uiFields),
    ...Object.keys(normalizedFields),
    ...Object.keys(engineFields),
  ]);

  return Array.from(allFieldKeys).sort().map((field): FieldExplorerRow => {
    const uiValue = uiFields[field];
    const normalizedValue = normalizedFields[field];
    const engineValue = engineFields[field];
    const mismatch = normalizeForCompare(uiValue) !== normalizeForCompare(engineValue);
    const derived = uiValue === undefined && (engineValue !== undefined || normalizedValue !== undefined);
    const warning = mismatch && (engineValue === undefined || normalizeForCompare(engineValue) === 'unknown');
    const scoreImpacting = scoreImpactFields.has(field) || scoreImpactFields.has(field.split('.').slice(-1)[0]);

    return {
      id: field,
      field,
      uiValue,
      normalizedValue,
      engineValue,
      mismatch,
      derived,
      warning,
      scoreImpacting,
      reasoning: [
        { stage: 'UI Snapshot', title: 'Captured UI field', detail: uiValue },
        { stage: 'Normalised', title: 'Normalised/intermediate value', detail: normalizedValue },
        { stage: 'Engine Input', title: 'Final engine input value', detail: engineValue },
      ],
    };
  });
}


function formatScoreBadge(score: number | null | undefined, optionId: string): string {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.warn(`[SystemOptionCard] Missing score for option ${optionId}; displaying placeholder`);
    }
    return '—';
  }

  return String(Math.round(Math.max(0, Math.min(100, score))));
}

function isCombiArchitectureCard(card: CardData): boolean {
  const normalized = card.id.replace(/_/g, '-').toLowerCase();
  return normalized === 'combi' || normalized === 'storage-combi';
}

function toRating(score?: number | null): 'Strong' | 'Improved' | 'Moderate' | 'Limited' {
  if (typeof score !== 'number' || !Number.isFinite(score)) return 'Moderate';
  if (score >= 80) return 'Strong';
  if (score >= 65) return 'Improved';
  if (score >= 45) return 'Moderate';
  return 'Limited';
}

function getShowerGuidance(card: CardData): { icon: string; title: string; text: string } {
  if (isCombiArchitectureCard(card)) {
    return {
      icon: '🚿',
      title: 'Combi hot-water profile',
      text: 'Flow limited by incoming mains rate. Performance can drop when multiple taps run simultaneously.',
    };
  }

  return {
    icon: '🚿🚿🚿',
    title: 'Stored hot-water profile',
    text: 'Comfort maintained for consecutive or multi-tap use when correctly sized.',
  };
}

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--hub-green, #22c55e)';
  if (score >= 60) return 'var(--hub-amber, #f59e0b)';
  if (score >= 40) return 'var(--hub-orange, #f97316)';
  return 'var(--hub-red, #ef4444)';
}

function ScoreBar({ label, score, weight, weightMultiplier }: { label: string; score: number; weight?: string; weightMultiplier?: number }) {
  // Clamp score to 0-100 range
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  
  // Build label suffix showing weight and contribution
  let labelSuffix = '';
  if (weight && weightMultiplier !== undefined) {
    const contribution = (score * weightMultiplier).toFixed(1);
    labelSuffix = ` (${weight} of total · contributes ${contribution})`;
  } else if (weight) {
    labelSuffix = ` (${weight} of total)`;
  }
  
  return (
    <div className="hub-score-bar">
      <div className="hub-score-bar__header">
        <span className="hub-score-bar__label">
          {label}{labelSuffix}
        </span>
        <span className="hub-score-bar__value" style={{ color: scoreColor(clamped) }}>
          {clamped}
        </span>
      </div>
      <div className="hub-score-bar__track">
        <div
          className="hub-score-bar__fill"
          style={{ width: `${clamped}%`, background: scoreColor(clamped) }}
        />
      </div>
    </div>
  );
}

function SubScoreRow({ label, score, secondaryScore, deltaLabel }: { label: string; score: number | null; secondaryScore?: number | null; deltaLabel?: string }) {
  if (score === null || !Number.isFinite(score)) {
    return (
      <div className="hub-subscore-row">
        <span className="hub-subscore-row__label">{label}</span>
        <div className="hub-subscore-row__bar-wrap">
          <span className="hub-subscore-row__value">Not assessed</span>
        </div>
      </div>
    );
  }

  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const hasSecondary = typeof secondaryScore === 'number' && Number.isFinite(secondaryScore);
  const secondaryClamped = hasSecondary
    ? Math.max(0, Math.min(100, Math.round(secondaryScore)))
    : null;
  const impliedDelta = hasSecondary && secondaryClamped !== null
    ? Math.max(0, clamped - secondaryClamped)
    : 0;
  const resolvedDelta = hasSecondary
    ? (deltaLabel ?? (impliedDelta > 0 ? `-${impliedDelta} if you skip recommended upgrades` : undefined))
    : undefined;

  return (
    <div className="hub-subscore-row">
      <span className="hub-subscore-row__label">{label}</span>
      <div className="hub-subscore-row__bar-wrap">
        <div className="hub-subscore-row__track">
          {hasSecondary && secondaryClamped !== null && (
            <div
              className="hub-subscore-row__fill hub-subscore-row__fill--secondary"
              style={{ width: `${secondaryClamped}%`, background: scoreColor(secondaryClamped) }}
            />
          )}
          <div
            className="hub-subscore-row__fill"
            style={{ width: `${clamped}%`, background: scoreColor(clamped) }}
          />
        </div>
        <span className="hub-subscore-row__value">{clamped}</span>
      </div>
      {resolvedDelta && <span className="hub-subscore-row__delta">{resolvedDelta}</span>}
    </div>
  );
}


export function EngineDebugPanel({ debug }: { debug: EngineDebugPayload }) {
  if (!debug) return null;

  const [selectedStage, setSelectedStage] = useState<PipelineStageId>('ui');
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showMismatchesOnly, setShowMismatchesOnly] = useState(false);
  const [showDerivedOnly, setShowDerivedOnly] = useState(false);
  const [showScoreImpactingOnly, setShowScoreImpactingOnly] = useState(false);

  const fields = useMemo(() => buildFieldExplorerRows(debug), [debug]);

  const filteredFields = fields.filter((row) => {
    if (showMismatchesOnly && !row.mismatch) return false;
    if (showDerivedOnly && !row.derived) return false;
    if (showScoreImpactingOnly && !row.scoreImpacting) return false;
    if (search.trim().length > 0) {
      const valueBlob = `${row.field} ${formatDebugValue(row.uiValue)} ${formatDebugValue(row.normalizedValue)} ${formatDebugValue(row.engineValue)}`.toLowerCase();
      if (!valueBlob.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });

  const selectedField = filteredFields.find((row) => row.id === selectedFieldId) ?? filteredFields[0] ?? null;

  const stageStats = useMemo(() => {
    const warningCount = fields.filter((row) => row.warning).length;
    const mismatchCount = fields.filter((row) => row.mismatch).length;
    const derivedCount = fields.filter((row) => row.derived).length;
    const fieldCount = fields.length;

    return {
      ui: { fieldCount, mismatchCount: 0, warningCount: 0, derivedCount: 0 },
      normalized: { fieldCount: Object.keys(flattenRecord(debug.componentBreakdown ?? {})).length, mismatchCount, warningCount: 0, derivedCount },
      enriched: { fieldCount: Array.isArray(debug.components) ? debug.components.length : 0, mismatchCount: 0, warningCount, derivedCount },
      'engine-input': { fieldCount: Object.keys(flattenRecord(debug.engineInput ?? {})).length, mismatchCount, warningCount, derivedCount },
      rules: { fieldCount: Array.isArray(debug.components) ? debug.components.length : 0, mismatchCount, warningCount, derivedCount },
      output: { fieldCount: debug.aggregation?.buckets?.length ?? 0, mismatchCount, warningCount, derivedCount },
    };
  }, [debug, fields]);

  const stageDetail = {
    ui: debug.context,
    normalized: debug.componentBreakdown ?? 'not provided',
    enriched: debug.constraints,
    'engine-input': debug.engineInput ?? 'not provided',
    rules: {
      components: debug.components,
      aggregation: debug.aggregation,
    },
    output: debug.aggregation,
  } as const;

  return (
    <div className="hub-option-breakdown">
      <div className="hub-option-breakdown__header">
        <div className="hub-option-breakdown__heading">Developer: Engine debug</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 1fr', gap: '0.75rem', alignItems: 'start' }}>
        <aside style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem' }}>
          {PIPELINE_STAGES.map((stage) => {
            const stats = stageStats[stage.id];
            const isActive = stage.id === selectedStage;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => setSelectedStage(stage.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.45rem 0.5rem',
                  marginBottom: '0.4rem',
                  borderRadius: 6,
                  border: isActive ? '1px solid #2563eb' : '1px solid #e5e7eb',
                  background: isActive ? '#eff6ff' : '#fff',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '0.78rem', fontWeight: 600 }}>{stage.icon} {stage.label}</div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{stats.fieldCount} fields · {stats.mismatchCount} mismatches</div>
                <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>{stats.warningCount > 0 ? `⚠ ${stats.warningCount}` : '✓ 0'} warnings · {stats.derivedCount} derived</div>
              </button>
            );
          })}
        </aside>

        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.6rem' }}>
          <div style={{ position: 'sticky', top: 0, background: '#fff', paddingBottom: '0.5rem' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fields (gc, pressure, insulation...)"
              style={{ width: '100%', padding: '0.45rem 0.55rem', borderRadius: 6, border: '1px solid #d1d5db' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap', fontSize: '0.75rem' }}>
              <label><input type="checkbox" checked={showMismatchesOnly} onChange={(e) => setShowMismatchesOnly(e.target.checked)} /> mismatches</label>
              <label><input type="checkbox" checked={showDerivedOnly} onChange={(e) => setShowDerivedOnly(e.target.checked)} /> derived</label>
              <label><input type="checkbox" checked={showScoreImpactingOnly} onChange={(e) => setShowScoreImpactingOnly(e.target.checked)} /> score-impacting</label>
              <button type="button" onClick={() => { setShowMismatchesOnly(false); setShowDerivedOnly(false); setShowScoreImpactingOnly(true); }} style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '0.15rem 0.5rem', background: '#f9fafb', cursor: 'pointer' }}>
                Why did you recommend X?
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '0.45rem', maxHeight: 420, overflow: 'auto' }}>
            {filteredFields.map((row) => {
              const selected = selectedField?.id === row.id;
              return (
                <button
                  type="button"
                  key={row.id}
                  onClick={() => setSelectedFieldId(row.id)}
                  style={{ textAlign: 'left', border: selected ? '1px solid #2563eb' : '1px solid #e5e7eb', background: selected ? '#eff6ff' : '#fff', borderRadius: 8, padding: '0.5rem', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <strong style={{ fontSize: '0.78rem' }}>{row.field}</strong>
                    <span style={{ fontSize: '0.68rem', color: row.warning ? '#dc2626' : row.mismatch ? '#d97706' : '#16a34a' }}>
                      {row.warning ? 'warning' : row.mismatch ? 'mismatch' : 'aligned'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', marginTop: '0.25rem' }}>UI: {formatDebugValue(row.uiValue)}</div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563' }}>Engine: {formatDebugValue(row.engineValue)}</div>
                  <div style={{ fontSize: '0.68rem', marginTop: '0.25rem', color: '#6b7280' }}>
                    {row.derived ? 'derived · ' : ''}{row.scoreImpacting ? 'score-impacting' : 'no score impact'}
                  </div>
                </button>
              );
            })}
            {filteredFields.length === 0 && <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>No fields match current filters.</div>}
          </div>
        </section>

        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.6rem' }}>
          <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.85rem' }}>Reasoning / Math</div>
          <div style={{ fontSize: '0.75rem', marginBottom: '0.5rem', color: '#4b5563' }}>
            Stage focus: <strong>{PIPELINE_STAGES.find((stage) => stage.id === selectedStage)?.label}</strong>
          </div>
          <pre style={{ marginTop: 0 }}>{formatDebugValue(stageDetail[selectedStage])}</pre>
          {selectedField && (
            <div style={{ marginTop: '0.65rem' }}>
              <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>Field drill-down: {selectedField.field}</div>
              {selectedField.reasoning.map((step, idx) => (
                <div key={`${selectedField.id}-${idx}`} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.45rem', marginTop: '0.35rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{step.stage}</div>
                  <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{step.title}</div>
                  <pre style={{ marginTop: '0.3rem' }}>{formatDebugValue(step.detail)}</pre>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export type SelectionRole = 'A' | 'B' | 'C' | null;

interface Props {
  card: CardData;
  selectionRole: SelectionRole;
  onSelect: (id: string) => void;
  onSelectB?: (id: string) => void;
  onSelectC?: (id: string) => void;
  isSelectedOption?: boolean;
  isSelectedOptionB?: boolean;
  isSelectedOptionC?: boolean;
  /** Premium add-ons for this option (when selected) */
  addOns?: ScopeItem[];
  onToggleAddOn?: (optionId: string, addOnId: string) => void;
  onNavigateSource?: (sourcePath: string) => void;
  defaultShowBreakdown?: boolean;
}

export function SystemOptionCard({ card, selectionRole, onSelect, onSelectB, onSelectC, isSelectedOption = false, isSelectedOptionB = false, isSelectedOptionC = false, addOns, onToggleAddOn, onNavigateSource, defaultShowBreakdown = false }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(defaultShowBreakdown);
  const [showAllReasons, setShowAllReasons] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const isUserSelected = isSelectedOption || isSelectedOptionB || isSelectedOptionC;
  const selected = selectionRole !== null || isUserSelected;
  const canCustomize = isSelectedOption;

  const hasBreakdown = !!card.scoreBreakdown;
  const explanationItems = normalizeExplanationItems(card);
  const topExplanationItems = explanationItems.slice(0, DEFAULT_VISIBLE_EXPLANATION_ITEMS);
  const narrativeHeadline = getNarrativeHeadline(card);
  const structuralSummary = getStructuralSummary(card);
  const groupedExplanationItems = explanationItems.reduce<Record<ExplanationItem['kind'], ExplanationItem[]>>((acc, item) => {
    acc[item.kind].push(item);
    return acc;
  }, { benefit: [], cost: [], work: [], assumption: [], note: [] });
  const showerGuidance = getShowerGuidance(card);

  return (
    <div
      className={`hub-option-card${selected ? ' hub-option-card--selected' : ''}${isSelectedOption ? ' hub-option-card--confirmed' : ''}${selectionRole ? ` hub-option-card--option-${selectionRole.toLowerCase()}` : ''}`}
    >
      <div className="hub-option-card__header">
        <span className="hub-option-card__label">
          {selectionRole && (
            <span className="hub-option-card__role-badge">
              Option {selectionRole}
            </span>
          )}
          {card.label}
        </span>
        <span 
          className={`hub-option-card__score hub-option-card__score--${card.suitability}${hasBreakdown ? ' hub-option-card__score--clickable' : ''}`}
          onClick={(e) => {
            if (hasBreakdown) {
              e.stopPropagation();
              setShowBreakdown(!showBreakdown);
            }
          }}
          title={hasBreakdown ? 'Click to see score breakdown' : undefined}
        >
          {formatScoreBadge(card.scoreBreakdown?.overallScore ?? card.suitabilityScore, card.id)}
        </span>
      </div>


      <div className="hub-option-card__verdict">
        {VERDICT_TEXT[card.suitability]}
        {card.confidence !== 'high' && ` · confidence: ${card.confidence}`}
        {typeof card.suitabilityScore === 'number' && Number.isFinite(card.suitabilityScore) && ` (${Math.round(card.suitabilityScore)}%)`}
      </div>

      <div className="hub-option-card__narrative" onClick={(e) => e.stopPropagation()}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{narrativeHeadline}</div>
        {structuralSummary.length > 0 && (
          <ul className="hub-option-card__bullets" style={{ marginBottom: 8 }}>
            {structuralSummary.map((line, idx) => <li key={`struct-${idx}`}>{line}</li>)}
          </ul>
        )}
      </div>

      {topExplanationItems.length > 0 && (
        <>
          <ul className="hub-option-card__bullets">
            {topExplanationItems.map((item) => (
              <li key={item.id}>
                {item.text}
                {isDebugViewEnabled() && (
                  <span className="hub-option-card__bullet-debug">
                    {' '}
                    [{item.sourceKey || 'unknown'}{item.impact !== undefined ? ` · impact ${item.impact}` : ''}]
                  </span>
                )}
              </li>
            ))}
          </ul>
          {explanationItems.length > DEFAULT_VISIBLE_EXPLANATION_ITEMS && (
            <button
              type="button"
              className="hub-option-card__show-all"
              onClick={(e) => {
                e.stopPropagation();
                setShowAllReasons((current) => !current);
              }}
            >
              {showAllReasons ? 'Hide full reasons' : `Show all reasons (${explanationItems.length})`}
            </button>
          )}
          {showAllReasons && (
            <div className="hub-option-card__all-reasons" onClick={(e) => e.stopPropagation()}>
              {(Object.keys(groupedExplanationItems) as Array<ExplanationItem['kind']>).map((kind) => {
                const items = groupedExplanationItems[kind];
                if (items.length === 0) return null;

                return (
                  <div key={kind} className="hub-option-card__all-reasons-group">
                    <div className="hub-option-card__all-reasons-heading">{EXPLANATION_GROUP_LABELS[kind]}</div>
                    <ul className="hub-option-card__bullets">
                      {items.map((item) => (
                        <li key={item.id}>
                          {item.text}
                          {isDebugViewEnabled() && (
                            <span className="hub-option-card__bullet-debug">
                              {' '}
                              [{item.sourceKey || 'unknown'}{item.impact !== undefined ? ` · impact ${item.impact}` : ''}]
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className="hub-option-card__shower-guidance" onClick={(e) => e.stopPropagation()}>
        <div className="hub-option-card__shower-icons" aria-hidden="true">{showerGuidance.icon}</div>
        <div>
          <div className="hub-option-card__shower-title">{showerGuidance.title}</div>
          <div className="hub-option-card__shower-copy">{showerGuidance.text}</div>
        </div>
      </div>

      {card.id.startsWith('ashp') && (card.redesignRequirements?.length ?? 0) > 0 && (
        <details className="hub-option-breakdown__advanced" onClick={(e) => e.stopPropagation()}>
          <summary>What would unlock a high score?</summary>
          <ul className="hub-option-card__bullets" style={{ marginTop: 8 }}>
            {card.redesignRequirements?.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </details>
      )}

      {/* Performance ranges from engine (PRs #440-#442) */}
      {card.performanceRanges && (
        <div className="hub-option-card__perf-ranges">
          {card.performanceRanges.scopConditional && (
            <div className="hub-option-card__perf-item">
              <span className="hub-option-card__perf-label">SCOP:</span>
              {card.performanceRanges.scopConditional.lowTempConfirmed ? (
                <span className="hub-option-card__perf-value">
                  {card.performanceRanges.scopConditional.withUpgrades.min}&ndash;{card.performanceRanges.scopConditional.withUpgrades.max}
                </span>
              ) : (
                <span className="hub-option-card__perf-value">
                  {card.performanceRanges.scopConditional.asIs.min}&ndash;{card.performanceRanges.scopConditional.asIs.max}
                  <span className="hub-option-card__perf-note">
                    (up to {card.performanceRanges.scopConditional.withUpgrades.min}&ndash;{card.performanceRanges.scopConditional.withUpgrades.max} with emitter upgrades)
                  </span>
                </span>
              )}
            </div>
          )}
          {card.performanceRanges.scop && !card.performanceRanges.scopConditional && (
            <div className="hub-option-card__perf-item">
              <span className="hub-option-card__perf-label">SCOP:</span>
              <span className="hub-option-card__perf-value">
                {card.performanceRanges.scop.min}&ndash;{card.performanceRanges.scop.max}
                {card.performanceRanges.scop.footnote && (
                  <span className="hub-option-card__perf-note">*</span>
                )}
              </span>
            </div>
          )}
          {card.performanceRanges.lifespan && (
            <div className="hub-option-card__perf-item">
              <span className="hub-option-card__perf-label">Expected lifespan:</span>
              <span className="hub-option-card__perf-value">
                {card.performanceRanges.lifespan.min}&ndash;{card.performanceRanges.lifespan.max} {card.performanceRanges.lifespan.unit}
              </span>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button
          className="hub-strip-btn"
          style={{ fontSize: 12, padding: '3px 10px' }}
          onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          {expanded ? 'Hide details' : 'Why?'}
        </button>
        {isDebugViewEnabled() && card.scoreBreakdown?.details?.debug && (
          <button
            className="hub-strip-btn"
            style={{ fontSize: 12, padding: '3px 10px' }}
            onClick={e => { e.stopPropagation(); setShowDebugPanel(!showDebugPanel); }}
          >
            {showDebugPanel ? 'Hide debug' : 'Debug'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="hub-option-explain">
          {card.assumptions.length > 0 && (
            <div className="hub-option-explain__section">
              <div className="hub-option-explain__heading">Assumptions</div>
              <ul className="hub-option-explain__list">
                {card.assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          {card.wouldNeedToChange.length > 0 && (
            <div className="hub-option-explain__section">
              <div className="hub-option-explain__heading">Would need to change to improve</div>
              <ul className="hub-option-explain__list">
                {card.wouldNeedToChange.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          {card.wontSolve.length > 0 && (
            <div className="hub-option-explain__section">
              <div className="hub-option-explain__heading">Definitely won&apos;t solve</div>
              <ul className="hub-option-explain__list">
                {card.wontSolve.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Score breakdown panel */}
      {showBreakdown && card.scoreBreakdown && (() => {
        const bd = card.scoreBreakdown;
        const hideStandingLoss = isCombiArchitectureCard(card);
        const techSubEntries = bd.details?.technicalSubScores
          ? Object.entries(bd.details.technicalSubScores).filter(([key]) => !(hideStandingLoss && key === 'standing_loss')) : [];
        const breakdownRows: ScoreBreakdownRow[] = bd.details?.breakdownRows ?? [];
        const technicalRows = breakdownRows.filter((row) => row.group === 'technical' && !(hideStandingLoss && row.key === 'standing_loss'));
        const priorityRows = breakdownRows.filter((row) => row.group === 'priority');
        const futureRows = breakdownRows.filter((row) => row.group === 'future');
        const priorityEntries = bd.details?.priorityScores
          ? Object.entries(bd.details.priorityScores) as Array<[string, number | null]> : [];
        const planEntries = bd.details?.futurePlanScores
          ? Object.entries(bd.details.futurePlanScores).filter(([k]) => k !== 'no_plans' && k !== 'unsure') : [];
        const planContexts = bd.details?.futurePlanContexts ?? {};
        const adjustments = bd.details?.confidenceAdjustments ?? [];
        const reasons = bd.details?.reasons ?? [];
        const killSwitches = bd.details?.killSwitchReasons ?? [];
        const penalties = bd.details?.penalties ?? [];
        const constraintReasons = bd.details?.constraintReasons ?? [];
        const hardReasons = constraintReasons.filter((r) => r.kind === 'hard');
        const softReasons = constraintReasons.filter((r) => r.kind === 'soft');

        return (
          <div
            className="hub-option-breakdown"
            onClick={e => e.stopPropagation()}
          >
            <div className="hub-option-breakdown__header">
              <div className="hub-option-breakdown__heading">What this means for your home</div>
              <button
                className="hub-option-breakdown__close"
                onClick={e => { e.stopPropagation(); setShowBreakdown(false); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Kill switches - show prominently at top if present */}
            {killSwitches.length > 0 && (
              <div className="hub-option-breakdown__section hub-option-breakdown__section--kill-switch">
                <div className="hub-option-breakdown__subheading hub-option-breakdown__subheading--alert">
                  ⚠️ Primary Reason for Low Score
                </div>
                <ul className="hub-option-breakdown__list hub-option-breakdown__list--error">
                  {killSwitches.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {hardReasons.length > 0 && (
              <div className="hub-option-breakdown__section hub-option-breakdown__section--kill-switch">
                <div className="hub-option-breakdown__subheading hub-option-breakdown__subheading--alert">
                  Not suitable for your preferences
                </div>
                <ul className="hub-option-breakdown__list hub-option-breakdown__list--error">
                  {hardReasons.map((reason) => (
                    <li key={reason.id}>✖ {reason.label}{reason.details ? ` — ${reason.details}` : ''}</li>
                  ))}
                </ul>
                {hardReasons.some((r) => !!r.sourcePath) && (
                  <button
                    className="hub-strip-btn"
                    onClick={() => {
                      const path = hardReasons.find((r) => !!r.sourcePath)?.sourcePath;
                      if (path && onNavigateSource) onNavigateSource(path);
                    }}
                  >
                    Change preferences
                  </button>
                )}
              </div>
            )}

            <div className="hub-breakdown-overall">
              <span className="hub-breakdown-overall__label">What This Means For Your Home</span>
            </div>

            <div className="hub-option-breakdown__customer-shell">
              <div className="hub-customer-metric"><span>Hot Water Comfort</span><strong>{toRating(bd.technicalFit)}</strong></div>
              <div className="hub-customer-metric"><span>Running Costs</span><strong>{toRating(bd.priorityFit)}</strong></div>
              <div className="hub-customer-metric"><span>Upgrade Disruption</span><strong>{card.wouldNeedToChange.length > 2 ? 'Moderate' : 'Limited'}</strong></div>
              <div className="hub-customer-metric"><span>Future Flexibility</span><strong>{toRating(bd.futureFit)}</strong></div>
            </div>

            <div className="hub-option-breakdown__section">
              <div className="hub-option-breakdown__subheading">Why Consider an Upgrade?</div>
              <ul className="hub-option-breakdown__list">
                <li>Open-vented circuits can allow oxygen ingress, which can increase corrosion risk over time.</li>
                <li>Basic controls can leave efficiency potential unused compared with load-compensating strategies.</li>
                <li>Legacy cylinders can have higher standing losses than modern insulated units.</li>
              </ul>
            </div>

            <details className="hub-option-breakdown__advanced">
              <summary>View Technical Breakdown (Advanced)</summary>
              <div className="hub-option-breakdown__content">
              {/* Technical Fit + sub-scores */}
              <ScoreBar label="Technical Fit" score={bd.technicalFit} weight="55%" weightMultiplier={0.55} />
              {(technicalRows.length > 0 || techSubEntries.length > 0) && (
                <div className="hub-option-breakdown__subsection">
                  {technicalRows.length > 0
                    ? technicalRows.map((row) => (
                      <SubScoreRow
                        key={row.key}
                        label={row.label || TECH_SUB_LABELS[row.key] || row.key}
                        score={row.scoreFull}
                        secondaryScore={row.scoreMin}
                        deltaLabel={row.deltaLabel}
                      />
                    ))
                    : techSubEntries.map(([key, score]) => (
                      <SubScoreRow key={key} label={TECH_SUB_LABELS[key] || key} score={score} />
                    ))}
                </div>
              )}

              {softReasons.length > 0 && (
                <div className="hub-option-breakdown__section">
                  <div className="hub-option-breakdown__subheading">Soft constraints</div>
                  <ul className="hub-option-breakdown__list hub-option-breakdown__list--warning">
                    {softReasons.map((reason) => (
                      <li key={reason.id}>{reason.label}{reason.details ? ` — ${reason.details}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Explicit penalties */}
              {penalties.length > 0 && (
                <div className="hub-option-breakdown__section hub-option-breakdown__section--penalties">
                  <div className="hub-option-breakdown__subheading">Constraints Detected</div>
                  <ul className="hub-option-breakdown__list hub-option-breakdown__list--penalty">
                    {penalties.map((penalty, i) => (
                      <li key={i}>
                        <strong>{penalty.dimension}:</strong> {penalty.penalty > 0 ? '+' : ''}{penalty.penalty} — {penalty.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Priority Fit + sub-scores */}
              {bd.priorityFit !== undefined && (
                <ScoreBar label="Priority Fit" score={bd.priorityFit} weight="30%" weightMultiplier={0.30} />
              )}
              {(priorityRows.length > 0 || priorityEntries.length > 0) && (
                <div className="hub-option-breakdown__subsection">
                  {priorityRows.length > 0
                    ? priorityRows.map((row) => (
                      <SubScoreRow
                        key={row.key}
                        label={row.label || PRIORITY_LABELS[row.key] || row.key}
                        score={row.scoreFull}
                        secondaryScore={row.scoreMin}
                        deltaLabel={row.deltaLabel}
                      />
                    ))
                    : priorityEntries.map(([key, score]) => (
                      <SubScoreRow key={key} label={PRIORITY_LABELS[key] || key} score={score} />
                    ))}
                </div>
              )}

              {/* Future Plans Fit + sub-scores with context */}
              {(bd.futureFitAssessed === false || bd.futureFit === null || bd.futureFit === undefined) ? (
                <div className="hub-option-breakdown__section">
                  <div className="hub-option-breakdown__subheading">Future Plans Fit</div>
                  <div className="hub-option-breakdown__context">Not assessed (add future plans to score this)</div>
                </div>
              ) : (
                <ScoreBar label="Future Plans Fit" score={bd.futureFit} weight="15%" weightMultiplier={0.15} />
              )}
              {(futureRows.length > 0 || planEntries.length > 0) && (
                <div className="hub-option-breakdown__subsection">
                  {futureRows.length > 0
                    ? futureRows.map((row) => (
                      <SubScoreRow
                        key={row.key}
                        label={row.label || PLAN_LABELS[row.key] || row.key}
                        score={row.scoreFull}
                        secondaryScore={row.scoreMin}
                        deltaLabel={row.deltaLabel}
                      />
                    ))
                    : planEntries.map(([key, score]) => (
                      <div key={key}>
                        <SubScoreRow label={PLAN_LABELS[key] || key} score={score} />
                        {planContexts[key] && (
                          <div className="hub-option-breakdown__context">
                            {planContexts[key]}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* Feasibility factor */}
              {bd.feasibility !== undefined && bd.feasibility < 1 && (
                <div className="hub-breakdown-feasibility">
                  <span className="hub-breakdown-feasibility__label">Feasibility Factor</span>
                  <span className="hub-breakdown-feasibility__value" style={{ color: scoreColor(bd.feasibility * 100) }}>
                    {(bd.feasibility * 100).toFixed(0)}%
                  </span>
                  <span className="hub-breakdown-feasibility__note">
                    (reduces overall score — constraints detected)
                  </span>
                </div>
              )}

              {/* Confidence adjustments */}
              {adjustments.length > 0 && (
                <div className="hub-option-breakdown__section">
                  <div className="hub-option-breakdown__subheading">Confidence Adjustments</div>
                  <ul className="hub-option-breakdown__list hub-option-breakdown__list--warning">
                    {adjustments.map((adj, i) => (
                      <li key={i}>{adj}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key factors / reasons */}
              {reasons.length > 0 && (
                <div className="hub-option-breakdown__section">
                  <div className="hub-option-breakdown__subheading">Key Factors</div>
                  <ul className="hub-option-breakdown__list">
                    {reasons.map((reason, i) => (
                      <li key={i}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              </div>
            </details>
          </div>
        );
      })()}


      {showDebugPanel && card.scoreBreakdown?.details?.debug && (
        <div onClick={e => e.stopPropagation()}>
          <EngineDebugPanel debug={card.scoreBreakdown.details.debug} />
        </div>
      )}

      {/* Select actions — user must explicitly choose an option */}
      <div className="hub-option-card__selection-actions">
        <button
          type="button"
          className={`hub-option-card__select-btn${isSelectedOption ? ' hub-option-card__select-btn--change' : ''}`}
          onClick={() => onSelect(card.id)}
        >
          {isSelectedOption ? '✓ Option A' : 'Select as A'}
        </button>
        {onSelectB && (
          <button
            type="button"
            className={`hub-option-card__select-btn${isSelectedOptionB ? ' hub-option-card__select-btn--change' : ''}`}
            onClick={() => onSelectB(card.id)}
          >
            {isSelectedOptionB ? '✓ Option B' : 'Select as B'}
          </button>
        )}
        {onSelectC && (
          <button
            type="button"
            className={`hub-option-card__select-btn${isSelectedOptionC ? ' hub-option-card__select-btn--change' : ''}`}
            onClick={() => onSelectC(card.id)}
          >
            {isSelectedOptionC ? '✓ Option C' : 'Select as C'}
          </button>
        )}
      </div>

      {/* Premium add-ons section — shown when this option is selected */}

      {selected && !canCustomize && (
        <div className="hub-option-card__addons">
          <div className="hub-option-card__addons-heading">Select as A above to customise upgrades</div>
        </div>
      )}

      {canCustomize && addOns && addOns.length > 0 && (
        <div className="hub-option-card__addons">
          <div className="hub-option-card__addons-heading">
            Premium add-ons for Option {selectionRole}
          </div>
          {addOns.map(addon => (
            <label key={addon.id} className={`hub-option-card__addon${addon.status === 'included' ? ' hub-option-card__addon--active' : ''}`}>
              <input
                type="checkbox"
                checked={addon.status === 'included'}
                onChange={() => onToggleAddOn?.(card.id, addon.id)}
              />
              <div className="hub-option-card__addon-info">
                <span className="hub-option-card__addon-label">{addon.label}</span>
                <span className="hub-option-card__addon-desc">{addon.description}</span>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
